// components/Success.js
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { auth, database } from '../firebase/firebaseConfig';
import { getAuthHeaders } from '../utils/auth-helpers';

const Success = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userId, setUserId] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [isConfirming, setIsConfirming] = useState(false);
  const [confirmationComplete, setConfirmationComplete] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile viewport
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Get session ID from URL on component mount
  useEffect(() => {
    const sessionIdFromUrl = searchParams.get('session_id');
    if (sessionIdFromUrl) {
      console.log("Session ID from URL:", sessionIdFromUrl);
      setSessionId(sessionIdFromUrl);
    }
  }, [searchParams]);

  // Get user data and existing session ID from Firebase
  useEffect(() => {
    if (!auth) {
      console.error("Auth not available");
      setError("Authentication service not available");
      setLoading(false);
      return;
    }

    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setUserId(user.uid);
        
        // If we already have sessionId from URL, don't need to fetch from Firebase
        if (sessionId) {
          setLoading(false);
          return;
        }

        // Otherwise, try to get it from Firebase
        if (!database) {
          console.error("Database not available");
          setError("Database service not available");
          setLoading(false);
          return;
        }

        const userRef = database.ref("users/" + user.uid);
        userRef.once('value')
          .then((snapshot) => {
            const userData = snapshot.val();
            if (userData && userData.subscription && userData.subscription.sessionId) {
              setSessionId(userData.subscription.sessionId);
            }
            setLoading(false);
          })
          .catch((err) => {
            console.error("Error fetching user data:", err);
            setError("Failed to fetch session data");
            setLoading(false);
          });
      } else {
        navigate('/');
      }
    });

    return () => unsubscribe();
  }, [navigate, sessionId]);

  // Auto-confirm payment after component loads
  useEffect(() => {
    const autoConfirmPayment = async () => {
      if (sessionId && userId && !loading && !isConfirming && !confirmationComplete) {
        console.log("Auto-confirming payment in 2 seconds...");
        
        // Wait 2 seconds to show success message first
        setTimeout(() => {
          handlePaymentSuccess();
        }, 2000);
      }
    };
    
    autoConfirmPayment();
  }, [sessionId, userId, loading, isConfirming, confirmationComplete]);

  const handlePaymentSuccess = async () => {
    if (!sessionId || !userId) {
      setError("Missing session or user information");
      return;
    }

    setIsConfirming(true);
    console.log("Confirming payment for session:", sessionId);
    const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';
    try {
      const headers = await getAuthHeaders();
      const response = await fetch(`${API_BASE_URL}/api/v1/payment-success`, {
        method: "POST",
        headers: headers,
        body: JSON.stringify({ 
          sessionId: sessionId, 
          firebaseId: userId 
        })
      });

      if (response.ok) {
        const data = await response.json();
        console.log("Payment confirmed:", data);
        setConfirmationComplete(true);
        
        // Navigate to home after showing success
        setTimeout(() => {
          navigate("/");
        }, 1500);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || "Payment confirmation failed");
      }
    } catch (error) {
      console.error("Payment confirmation error:", error);
      setError(error.message || "Failed to confirm payment");
      setIsConfirming(false);
    }
  };

  if (loading) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        backgroundColor: '#f9fafb',
        padding: isMobile ? '20px' : '0'
      }}>
        <div style={{
          textAlign: 'center',
          padding: isMobile ? '30px' : '40px',
          backgroundColor: 'white',
          borderRadius: isMobile ? '12px' : '16px',
          boxShadow: '0 10px 50px rgba(0, 0, 0, 0.1)',
          maxWidth: isMobile ? '90%' : '100%',
          width: isMobile ? '100%' : 'auto'
        }}>
          <div style={{
            width: isMobile ? '50px' : '60px',
            height: isMobile ? '50px' : '60px',
            margin: '0 auto 20px',
            border: '4px solid rgba(59, 130, 246, 0.2)',
            borderTopColor: '#3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }}></div>
          <p style={{ 
            color: '#6b7280', 
            fontSize: isMobile ? '16px' : '18px' 
          }}>Loading payment details...</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f9fafb',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: isMobile ? '20px' : '0'
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: isMobile ? '40px 24px' : '60px 40px',
        borderRadius: isMobile ? '12px' : '16px',
        boxShadow: '0 10px 50px rgba(0, 0, 0, 0.1)',
        textAlign: 'center',
        maxWidth: isMobile ? '100%' : '500px',
        width: isMobile ? '100%' : '90%'
      }}>
        {/* Success Icon */}
        <div style={{
          width: isMobile ? '60px' : '80px',
          height: isMobile ? '60px' : '80px',
          backgroundColor: '#10b981',
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: `0 auto ${isMobile ? '24px' : '30px'}`,
          boxShadow: '0 4px 14px rgba(16, 185, 129, 0.3)'
        }}>
          <svg 
            width={isMobile ? "30" : "40"} 
            height={isMobile ? "30" : "40"} 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="white" 
            strokeWidth="3" 
            strokeLinecap="round" 
            strokeLinejoin="round"
          >
            <polyline points="20 6 9 17 4 12"></polyline>
          </svg>
        </div>

        <h1 style={{
          fontSize: isMobile ? '24px' : '32px',
          fontWeight: '700',
          color: '#1f2937',
          marginBottom: isMobile ? '12px' : '16px'
        }}>
          Payment Successful!
        </h1>

        <p style={{
          fontSize: isMobile ? '16px' : '18px',
          color: '#6b7280',
          marginBottom: isMobile ? '30px' : '40px',
          lineHeight: '1.6'
        }}>
          {confirmationComplete 
            ? "Your subscription is now active. Redirecting..."
            : isConfirming 
            ? "Confirming your subscription..."
            : "Setting up your subscription..."}
        </p>

        {error && (
          <div style={{
            backgroundColor: '#fee2e2',
            border: '1px solid #fecaca',
            color: '#dc2626',
            padding: isMobile ? '10px 16px' : '12px 20px',
            borderRadius: '8px',
            marginBottom: '20px',
            fontSize: isMobile ? '13px' : '14px'
          }}>
            {error}
          </div>
        )}

        {/* Progress indicator */}
        {(isConfirming || !confirmationComplete) && !error && (
          <div style={{
            display: 'flex',
            justifyContent: 'center',
            gap: '8px',
            marginTop: '20px'
          }}>
            <div style={{
              width: isMobile ? '6px' : '8px',
              height: isMobile ? '6px' : '8px',
              borderRadius: '50%',
              backgroundColor: '#3b82f6',
              animation: 'pulse 1.4s infinite'
            }}></div>
            <div style={{
              width: isMobile ? '6px' : '8px',
              height: isMobile ? '6px' : '8px',
              borderRadius: '50%',
              backgroundColor: '#3b82f6',
              animation: 'pulse 1.4s infinite 0.2s'
            }}></div>
            <div style={{
              width: isMobile ? '6px' : '8px',
              height: isMobile ? '6px' : '8px',
              borderRadius: '50%',
              backgroundColor: '#3b82f6',
              animation: 'pulse 1.4s infinite 0.4s'
            }}></div>
          </div>
        )}

        {/* Manual retry button if there's an error */}
        {error && (
          <button
            onClick={handlePaymentSuccess}
            style={{
              marginTop: '20px',
              padding: isMobile ? '10px 20px' : '12px 24px',
              backgroundColor: '#3d5fc4',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: isMobile ? '14px' : '16px',
              fontWeight: '600',
              cursor: 'pointer'
            }}
          >
            Retry Confirmation
          </button>
        )}
      </div>

      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          
          @keyframes pulse {
            0%, 80%, 100% {
              opacity: 0.3;
              transform: scale(0.8);
            }
            40% {
              opacity: 1;
              transform: scale(1);
            }
          }
        `}
      </style>
    </div>
  );
};

export default Success;